import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { FileUp, Loader2, RefreshCcw } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useActiveCompany, pickActiveYear } from "../../context/ActiveCompanyContext";
import PageHeader from "../../components/PageHeader";
import EmptyState from "../../components/EmptyState";
import BatchDetail from "../../components/actuals/BatchDetail";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function ActualImportPage() {
  const { user } = useAuth();
  const canWrite = user?.role !== "READ_ONLY_MANAGEMENT";
  const [searchParams] = useSearchParams();
  const [companies, setCompanies] = useState([]);
  const { companyId, setCompanyId, year, setYear } = useActiveCompany();
  const [years, setYears] = useState([]);
  const [month, setMonth] = useState(null);
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [replaceMode, setReplaceMode] = useState(false);
  const [replaceComment, setReplaceComment] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/companies")
      .then((c) => {
        setCompanies(c.data);
        if (c.data.length > 0 && !c.data.some((co) => co.id === companyId)) setCompanyId(c.data[0].id);
      })
      .catch((e) => toast.error(apiError(e)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!companyId) return;
    api.get(`/companies/${companyId}/periods/years`)
      .then((res) => {
        setYears(res.data);
        const pick = pickActiveYear(res.data, year);
        if (pick !== year) setYear(pick);
      })
      .catch((e) => toast.error(apiError(e)));
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadBatch = (id) =>
    api.get(`/actuals/batches/${id}`).then((res) => setBatch(res.data)).catch((e) => toast.error(apiError(e)));

  // Deep link from Import History (?batch=<id>)
  useEffect(() => {
    const id = searchParams.get("batch");
    if (id) {
      setLoading(true);
      api.get(`/actuals/batches/${id}`)
        .then((res) => {
          setBatch(res.data);
          setCompanyId(res.data.company_id);
          setYear(res.data.year);
          setMonth(res.data.month);
        })
        .catch((e) => toast.error(apiError(e)))
        .finally(() => setLoading(false));
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load the active (non-superseded) batch for the selected month, if any
  useEffect(() => {
    if (!companyId || !year || !month || searchParams.get("batch")) return;
    setLoading(true);
    api.get("/actuals/batches", { params: { company_id: companyId, year, month } })
      .then((res) => {
        const active = res.data.filter((b) => b.status !== "SUPERSEDED");
        if (active.length > 0) return loadBatch(active[0].id);
        setBatch(null);
      })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [companyId, year, month]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) {
      toast.error("Choose the accounting P&L PDF first");
      return;
    }
    const fd = new FormData();
    fd.append("company_id", companyId);
    fd.append("year", String(year));
    fd.append("month", String(month));
    fd.append("file", file);
    if (replaceMode && batch) {
      fd.append("replace_batch_id", batch.id);
      fd.append("replacement_comment", replaceComment);
    }
    setUploading(true);
    try {
      const res = await api.post("/actuals/import", fd);
      toast.success("Report imported — review the preview below");
      setBatch(res.data);
      setReplaceMode(false);
      setReplaceComment("");
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid="actual-import-page">
      <PageHeader
        title="Import Accounting P&L"
        subtitle="Import the monthly accounting result (resultaträkning) as PDF. Only 'Perioden' values are imported — never 'Ackumulerat'."
        actions={
          <div className="flex items-center gap-2">
            <Select value={companyId || ""} onValueChange={setCompanyId}>
              <SelectTrigger className="w-56" data-testid="actual-company-select">
                <SelectValue placeholder="Select company" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {years.length > 0 && (
              <Select value={year ? String(year) : ""} onValueChange={(v) => { setYear(Number(v)); setMonth(null); }}>
                <SelectTrigger className="w-28" data-testid="actual-year-select"><SelectValue placeholder="Year" /></SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={String(y)} data-testid={`actual-year-${y}`}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {year && (
              <Select value={month ? String(month) : ""} onValueChange={(v) => setMonth(Number(v))}>
                <SelectTrigger className="w-32" data-testid="actual-month-select"><SelectValue placeholder="Month" /></SelectTrigger>
                <SelectContent>
                  {MONTHS.map((m, i) => (
                    <SelectItem key={i + 1} value={String(i + 1)} data-testid={`actual-month-${i + 1}`}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        }
      />

      {!month && (
        <div className="bg-white border border-slate-200 rounded-lg" data-testid="actual-import-empty">
          <EmptyState title="Select company, year and month" description="The selected month is validated against the report period in the PDF." testId="actual-import-empty-state" />
        </div>
      )}

      {month && loading && !batch && (
        <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
      )}

      {month && canWrite && (
        <div className="bg-white border border-slate-200 rounded-lg p-5 mb-5" data-testid="actual-upload-card">
          <div className="flex items-end gap-3 flex-wrap">
            <div className="space-y-1.5">
              <Label>Accounting P&L report (PDF)</Label>
              <input
                ref={fileRef}
                type="file"
                accept="application/pdf"
                className="block text-sm text-slate-600 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border file:border-slate-200 file:text-sm file:bg-white file:text-slate-700 hover:file:bg-slate-50"
                data-testid="actual-file-input"
              />
            </div>
            <Button onClick={upload} disabled={uploading || (replaceMode && !replaceComment.trim())}
              className="bg-[#0052CC] hover:bg-[#0047B3]" data-testid="actual-upload-button">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <FileUp className="h-4 w-4 mr-1.5" />}
              {replaceMode ? "Replace import" : "Import report"}
            </Button>
            {batch && !replaceMode && (
              <Button variant="outline" onClick={() => setReplaceMode(true)} data-testid="actual-replace-mode-button">
                <RefreshCcw className="h-4 w-4 mr-1.5" /> Replace this import
              </Button>
            )}
            {replaceMode && (
              <Button variant="ghost" onClick={() => { setReplaceMode(false); setReplaceComment(""); }} data-testid="actual-replace-cancel">
                Cancel replacement
              </Button>
            )}
          </div>
          {replaceMode && (
            <div className="mt-3 space-y-1.5" data-testid="actual-replace-form">
              <Label>Replacement reason (mandatory — stored with user and timestamp)</Label>
              <textarea
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm min-h-16"
                value={replaceComment}
                onChange={(e) => setReplaceComment(e.target.value)}
                placeholder="Why is the existing import being replaced?"
                data-testid="actual-replace-comment-input"
              />
              {batch?.status === "COMMITTED" && (
                <p className="text-xs text-amber-700">The committed batch stays authoritative until the replacement is verified and committed.</p>
              )}
            </div>
          )}
        </div>
      )}

      {month && batch && (
        <BatchDetail
          batch={batch}
          canWrite={canWrite}
          onUpdated={setBatch}
          onDeleted={() => setBatch(null)}
        />
      )}
    </div>
  );
}
